import React, { useCallback } from 'react';
import {
  Link,
  AddButton,
  AppContent,
  ButtonGroup,
  Icon,
  Tabs,
} from '@common/components';
import { setBreadcrumbs } from 'works/legacy/lib/client-store';
import { useNavigate, useParams } from '@common/hooks';
import { ShiftOpeningsList, ShiftsCalendar } from '@scheduling/components';
import { Dropdown, Row, Typography } from 'antd';
import { ShiftMessages } from '@scheduling/components/shift-messages';
import { useCurrentMembership, useCurrentEntity } from '@identity/hooks';
import { buildDialogUrl } from '@common/services';

const { Text } = Typography;

export function ShiftPage() {
  const navigate = useNavigate();
  const { type = 'feed' } = useParams<{ type: string }>();
  setBreadcrumbs([{ name: 'Shifts', path: '/works/shifts' }]);

  const currentMembership = useCurrentMembership();
  const currentEntity = useCurrentEntity();
  const onDemandEnabled = currentEntity.onDemandEnabled;

  const handleTabChange = useCallback(
    (key: React.Key) => {
      navigate(`/works/shifts/${key}`);
    },
    [navigate]
  );

  const canCreate = currentMembership.canCreateShiftGaps;
  const canUploadShiftGaps = currentMembership.canUploadShiftGaps;
  const createShiftDrawerUrl = buildDialogUrl('create-shift-drawer');

  const dropDownItems = [
    {
      key: 'add',
      label: (
        <Link to={createShiftDrawerUrl}>
          <Icon icon="plus" />
          <Text> Create Shift</Text>
        </Link>
      ),
    },
    {
      key: 'add-multiple',
      label: (
        <Link to="/works/shifts/feed/add-multiple">
          <Icon kind="regular" icon="arrowUpFromBracket" />
          <Text> Upload Shifts</Text>
        </Link>
      ),
    },
  ];

  const ActionButtons = () => {
    const shiftTabs = type === 'feed' || type === 'calendar';
    const showAdd = canCreate && canUploadShiftGaps && shiftTabs;
    return (
      <>
        <Row className="action-buttons">
          <ButtonGroup>
            {type === 'feed' && (
              <span id="wk-portal-target:org-shift-list-extra-actions" />
            )}
            {showAdd && (
              <Dropdown
                menu={{ items: dropDownItems }}
                className="shift-dropdown"
              >
                <AddButton showDropdown onClick={(e) => e.preventDefault()}>
                  Shifts
                </AddButton>
              </Dropdown>
            )}
            {canCreate && !canUploadShiftGaps && shiftTabs && (
              <Link to={createShiftDrawerUrl}>
                <AddButton>Shifts</AddButton>
              </Link>
            )}
          </ButtonGroup>
        </Row>
        <style jsx>{`
          :global(.ShiftTabs .ant-tabs-extra-content) {
            line-height: 32px;
            align-self: flex-start;
          }
          :global(.shift-dropdown:hover) {
            opacity: 90%;
          }
          :global(.action-buttons) {
            gap: 8px;
          }
        `}</style>
      </>
    );
  };

  return (
    <AppContent>
      {!!onDemandEnabled && (
        <>
          <Tabs
            className="ShiftTabs"
            onChange={handleTabChange}
            activeKey={type}
            tabBarExtraContent={<ActionButtons />}
            items={[
              {
                label: 'Calendar',
                key: 'calendar',
                children: <ShiftsCalendar />,
              },
              {
                label: 'List',
                key: 'feed',
                children: <ShiftOpeningsList triggerPortal={type} />,
              },
              {
                label: 'Messages',
                key: 'messages',
                children: <ShiftMessages />,
              },
            ]}
          ></Tabs>
        </>
      )}
    </AppContent>
  );
}
